/**
 * Quick View Modal Component
 * Displays product details in a modal overlay from collection pages
 */

class QuickViewModal {
  constructor() {
    this.modal = null;
    this.currentProductUrl = null;
    this.init();
  }

  init() {
    this.createModal();
    this.bindEvents();
  }

  createModal() {
    // Create modal structure
    const modal = document.createElement('div');
    modal.className = 'quick-view-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Quick view product details');

    modal.innerHTML = `
      <div class="quick-view-modal__overlay"></div>
      <div class="quick-view-modal__content">
        <button class="quick-view-modal__close" aria-label="Close quick view">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div class="quick-view-modal__loading">
          <div class="loading-spinner"></div>
        </div>
        <div class="quick-view-modal__body"></div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modal = modal;
  }

  bindEvents() {
    // Close on overlay click
    const overlay = this.modal.querySelector('.quick-view-modal__overlay');
    overlay.addEventListener('click', () => this.close());

    // Close button
    const closeBtn = this.modal.querySelector('.quick-view-modal__close');
    closeBtn.addEventListener('click', () => this.close());

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });

    // Delegate quick view button clicks
    document.addEventListener('click', (e) => {
      const quickViewBtn = e.target.closest('[data-quick-view-button]');
      if (quickViewBtn) {
        e.preventDefault();
        const productUrl = quickViewBtn.dataset.productUrl;
        if (productUrl) {
          this.open(productUrl);
        }
      }
    });
  }

  async open(productUrl) {
    if (!productUrl) return;

    this.currentProductUrl = productUrl;
    const body = this.modal.querySelector('.quick-view-modal__body');
    const loading = this.modal.querySelector('.quick-view-modal__loading');
    const content = this.modal.querySelector('.quick-view-modal__content');

    // Show modal
    this.modal.classList.add('active');
    document.body.classList.add('quick-view-modal-open');

    // Show loading state
    body.innerHTML = '';
    loading.style.display = 'block';
    content.classList.add('loading');

    try {
      // Fetch product data
      const response = await fetch(`${productUrl}?section_id=main-product`);
      const html = await response.text();

      // Parse the response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract product data
      const productData = this.extractProductData(doc);

      // Render modal content
      this.renderContent(productData);

      // Hide loading
      loading.style.display = 'none';
      content.classList.remove('loading');

      // Focus on close button for accessibility
      this.modal.querySelector('.quick-view-modal__close').focus();

    } catch (error) {
      console.error('Error loading quick view:', error);
      body.innerHTML = `
        <div style="padding: 3.2rem; text-align: center;">
          <p>Unable to load product details. Please try again.</p>
          <a href="${productUrl}" class="button button--primary" style="margin-top: 1.6rem;">View Product Page</a>
        </div>
      `;
      loading.style.display = 'none';
      content.classList.remove('loading');
    }
  }

  extractProductData(doc) {
    const productSection = doc.querySelector('[data-section*="main-product"]');

    return {
      title: doc.querySelector('.product__title')?.textContent?.trim() || '',
      vendor: doc.querySelector('.product__text.caption-with-letter-spacing')?.textContent?.trim() || '',
      price: doc.querySelector('#price-template--main')?.innerHTML || '',
      description: doc.querySelector('.product__description')?.innerHTML || '',
      media: this.extractMedia(doc),
      variants: doc.querySelector('variant-selects') || doc.querySelector('variant-radios'),
      productForm: doc.querySelector('product-form'),
      url: this.currentProductUrl
    };
  }

  extractMedia(doc) {
    const mediaItems = doc.querySelectorAll('.product__media-item');
    const media = [];

    mediaItems.forEach((item, index) => {
      const img = item.querySelector('img');
      if (img) {
        media.push({
          src: img.src,
          alt: img.alt || '',
          srcset: img.srcset || '',
          index: index
        });
      }
    });

    return media;
  }

  renderContent(data) {
    const body = this.modal.querySelector('.quick-view-modal__body');

    body.innerHTML = `
      <div class="quick-view-modal__media">
        ${this.renderMedia(data.media)}
      </div>
      <div class="quick-view-modal__info">
        ${data.vendor ? `<div class="quick-view-modal__vendor">${data.vendor}</div>` : ''}
        <h2 class="quick-view-modal__title">${data.title}</h2>
        <div class="quick-view-modal__price">
          ${data.price}
        </div>
        ${data.description ? `<div class="quick-view-modal__description">${this.truncateDescription(data.description)}</div>` : ''}
        <div class="quick-view-modal__variants">
          ${this.renderVariants(data.variants)}
        </div>
        <div class="quick-view-modal__form">
          ${this.renderForm(data.productForm)}
        </div>
        <a href="${data.url}" class="quick-view-modal__view-full">
          View full details
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    `;

    // Initialize media gallery interaction
    this.initializeMediaGallery();

    // Initialize product form if present
    this.initializeProductForm();
  }

  renderMedia(media) {
    if (!media || media.length === 0) {
      return '<div class="quick-view-modal__media-main"><div style="padding: 3.2rem; text-align: center; color: rgba(var(--color-foreground), 0.5);">No image available</div></div>';
    }

    const mainImage = media[0];

    return `
      <div class="quick-view-modal__media-main" data-main-image>
        <img
          src="${mainImage.src}"
          alt="${mainImage.alt}"
          ${mainImage.srcset ? `srcset="${mainImage.srcset}"` : ''}
          loading="eager"
        />
      </div>
      ${media.length > 1 ? `
        <div class="quick-view-modal__media-thumbnails">
          ${media.map((item, index) => `
            <button
              class="quick-view-modal__thumbnail ${index === 0 ? 'active' : ''}"
              data-thumbnail-index="${index}"
              aria-label="View image ${index + 1}"
            >
              <img src="${item.src}" alt="${item.alt}" loading="lazy" />
            </button>
          `).join('')}
        </div>
      ` : ''}
    `;
  }

  renderVariants(variantsElement) {
    if (!variantsElement) return '';

    // Clone the variant selector to avoid modifying the original
    const cloned = variantsElement.cloneNode(true);

    // Remove any scripts to avoid duplicate execution
    cloned.querySelectorAll('script').forEach(script => script.remove());

    return cloned.outerHTML;
  }

  renderForm(formElement) {
    if (!formElement) return '<p>Product form not available</p>';

    // Clone the form
    const cloned = formElement.cloneNode(true);

    // Remove any scripts
    cloned.querySelectorAll('script').forEach(script => script.remove());

    return cloned.outerHTML;
  }

  truncateDescription(html) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';

    if (text.length > 250) {
      return text.substring(0, 250) + '...';
    }

    return html;
  }

  initializeMediaGallery() {
    const thumbnails = this.modal.querySelectorAll('[data-thumbnail-index]');
    const mainImage = this.modal.querySelector('[data-main-image] img');

    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        const index = parseInt(thumbnail.dataset.thumbnailIndex);
        const img = thumbnail.querySelector('img');

        if (img && mainImage) {
          mainImage.src = img.src;
          mainImage.alt = img.alt;

          // Update active state
          thumbnails.forEach(t => t.classList.remove('active'));
          thumbnail.classList.add('active');
        }
      });
    });
  }

  initializeProductForm() {
    // Reinitialize variant selectors if present
    const variantSelects = this.modal.querySelector('variant-selects');
    const variantRadios = this.modal.querySelector('variant-radios');

    if (variantSelects && typeof VariantSelects !== 'undefined') {
      // Trigger change events on variant selects
      const selects = variantSelects.querySelectorAll('select');
      selects.forEach(select => {
        select.addEventListener('change', (e) => {
          this.handleVariantChange(e.target);
        });
      });
    }

    if (variantRadios && typeof VariantRadios !== 'undefined') {
      // Trigger change events on variant radios
      const radios = variantRadios.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          this.handleVariantChange(e.target);
        });
      });
    }

    // Initialize product form
    const productForm = this.modal.querySelector('product-form');
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAddToCart(productForm);
      });
    }
  }

  handleVariantChange(element) {
    // Let the native variant selector handle the change
    // Price and availability will update automatically
  }

  async handleAddToCart(form) {
    const formData = new FormData(form.querySelector('form'));
    const submitButton = form.querySelector('[type="submit"]');

    if (submitButton) {
      submitButton.disabled = true;
      const originalText = submitButton.textContent;
      submitButton.textContent = 'Adding...';

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          submitButton.textContent = 'Added!';

          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:item-added'));

          // Close modal after short delay
          setTimeout(() => {
            this.close();
          }, 1000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        submitButton.textContent = 'Error - Try Again';
        setTimeout(() => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
        }, 2000);
      }
    }
  }

  close() {
    this.modal.classList.remove('active');
    document.body.classList.remove('quick-view-modal-open');
    this.currentProductUrl = null;

    // Clear content after animation
    setTimeout(() => {
      const body = this.modal.querySelector('.quick-view-modal__body');
      body.innerHTML = '';
    }, 300);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickViewModal();
  });
} else {
  new QuickViewModal();
}
